import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
  useMemo,
} from 'react';
// Importar servicios existentes para las peticiones
import { fetchIntensityCategories, fetchRanking } from '../services/ratingsService';
import { fetchTrainingCategories } from '../services/trainingsService';
import { fetchCategoryWods, fetchWodDelDia } from '../services/wodsService';
import { fetchCurrentDateTime } from '../services/boxService';
import { CategoryAgent } from '../models/category';
import { IntensityCategory } from '../models/IntensityCategory';
import { Wod } from '../models/Wod';
import {
  NoWodError,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
  NetworkError,
  ApiError,
  InvalidResponseError,
} from '../services/errors';
import { CategoryWod } from '../models/CategoryWod';
import { RankingItem } from '../models/RankingItem';
import { Block, parseBlock } from '../models/Block';
import { parseParticipant, Participant } from '../models/Participant';
import { fetchParticipantes } from '../services/participantsService';
import pLimit from 'p-limit';

// Definición de la forma del contexto con todos los datos compartidos
interface DataContextProps {
  participantsVersion: number;
  loading: boolean;
  refreshing: boolean;
  today: string;
  nowTime: string;
  //
  wodDelDia: Wod[] | null;
  blocks: Block[];
  categoriesAgent: CategoryAgent[];
  categoryWods: CategoryWod[];
  //
  intensityCategories: IntensityCategory[];
  rankingsByBlock: Record<number, Record<number, Record<number, RankingItem[]>>>;
  //
  error: string | null;
  //
  getRanking: (
    blockId: number,
    intensityCategoryId: number,
    ratingTypeId: number,
  ) => RankingItem[] | undefined;
  getIntensityCategoryName: (intensityCategoryId: number) => string | undefined;
  getAgentCategory: () => number[];
  fetchParticipantsForSchedules: (
    schedules: number[] | undefined,
    date: string,
    time: string,
  ) => Promise<Record<number, Participant[]>>;
}

// Crear contexto con valor inicial indefinido
const DataContext = createContext<DataContextProps | undefined>(undefined);

// Props del proveedor incluyendo boxId
interface DataProviderProps {
  boxId: number;
  children: ReactNode;
}

// Componente DataProvider
export const DataProvider: React.FC<DataProviderProps> = ({ boxId, children }) => {
  // limit memoizado para mantener la misma referencia
  const limit = useMemo(() => pLimit(3), []);

  // Caché de participantes (ref, no causa re-renders)
  const participantsCacheRef = useRef<Record<string, Participant[]>>({});
  // Offset de tiempo del servidor (ms)
  const timeOffsetRef = useRef<number>(0);

  const makeKey = (scheduleId: number, date: string, time: string) => {
    // Truncar segundos: "11:24:14" -> "11:24"
    const timeWithoutSeconds = time.split(':').slice(0, 2).join(':');
    return `${date}__${timeWithoutSeconds}__${scheduleId}`;
  };

  const [categoriesAgent, setCategoriesAgent] = useState<CategoryAgent[]>([]);
  const [categoryWods, setCategoryWods] = useState<CategoryWod[]>([]);
  const [intensityCategories, setIntensityCategories] = useState<IntensityCategory[]>([]);
  const [wodDelDia, setWodDelDia] = useState<Wod[] | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);
  // rankingsByBlock: bloqueId -> intensidadId -> ratingTypeId -> arreglo de RankingItem
  const [rankingsByBlock, setRankingsByBlock] = useState<
    Record<number, Record<number, Record<number, RankingItem[]>>>
  >({});

  const [loading, setLoading] = useState<boolean>(true); // solo para primera carga
  const [refreshing, setRefreshing] = useState<boolean>(false); // para las recargas silenciosas
  const [participantsVersion, setParticipantsVersion] = useState<number>(0);

  const getLocalDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const [today, setToday] = useState(getLocalDate());
  const [nowTime, setNowTime] = useState(new Date().toTimeString().slice(0, 8));

  useEffect(() => {
    const id = setInterval(() => {
      const liveTime = new Date(Date.now() + timeOffsetRef.current);
      setNowTime(liveTime.toTimeString().slice(0, 8));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const [error, setError] = useState<string | null>(null);

  // isFetchingRef para evitar fetches concurrentes
  const isFetchingRef = useRef(false);

  /**
   * fetchParticipantsForSchedules
   * - schedules: array de schedule ids
   * - date, time: strings
   * Retorna un map scheduleId -> Participant[]
   */
  const fetchParticipantsForSchedules = useCallback(
    async (
      schedules: number[] | undefined,
      date: string,
      time: string,
    ): Promise<Record<number, Participant[]>> => {
      const result: Record<number, Participant[]> = {};
      if (!schedules || schedules.length === 0) return result;

      const toFetch: number[] = [];

      // 1) rellena con lo que haya en cache
      schedules.forEach((sid) => {
        const key = makeKey(sid, date, time);
        const cached = participantsCacheRef.current[key];
        if (cached) {
          result[sid] = cached;
        } else {
          toFetch.push(sid);
        }
      });

      // 2) fetch paralelo solo de los que faltan (con control de concurrencia)
      if (toFetch.length > 0) {
        const promises = toFetch.map((schedId) =>
          fetchParticipantes(boxId, 'now', time, schedId)
            .then((raw) => raw.map(parseParticipant))
            .catch((err) => {
              console.warn(`Error fetch participantes schedule ${schedId}:`, err);
              return [] as Participant[];
            }),
        );

        const resolved = await Promise.all(promises);

        toFetch.forEach((schedId, idx) => {
          const arr = resolved[idx] ?? [];
          const key = makeKey(schedId, date, time);
          participantsCacheRef.current[key] = arr; // guardamos en ref (no re-render)
          result[sidToKey(schedId)] = arr; // helper below maps schedId to result key
        });
      }

      return result;

      // helper function local para mantener typescript limpio (no cambia fuera)
      function sidToKey(sid: number) {
        return sid;
      }
    },
    [boxId],
  );

  // getRanking, getIntensityCategoryName, getAgentCategory memoizadas
  const getRanking = useCallback(
    (
      blockId: number,
      intensityCategoryId: number,
      ratingTypeId: number,
    ): RankingItem[] | undefined => {
      return rankingsByBlock[blockId]?.[intensityCategoryId]?.[ratingTypeId];
    },
    [rankingsByBlock],
  );

  const getIntensityCategoryName = useCallback(
    (intensityCategoryId: number) => {
      return intensityCategories.find((ic) => ic.id === intensityCategoryId)?.category;
    },
    [intensityCategories],
  );

  const getAgentCategory = useCallback(() => {
    return categoriesAgent.map((c) => c.id);
  }, [categoriesAgent]);

  // memoiza el context value -> evita que se recree en cada render
  const contextValue = useMemo<DataContextProps>(
    () => ({
      participantsVersion,
      categoriesAgent,
      categoryWods,
      intensityCategories,
      wodDelDia,
      blocks,
      rankingsByBlock,
      loading,
      refreshing,
      error,
      today,
      nowTime,
      getRanking,
      getIntensityCategoryName,
      getAgentCategory,
      fetchParticipantsForSchedules,
    }),
    [
      participantsVersion,
      categoriesAgent,
      categoryWods,
      intensityCategories,
      wodDelDia,
      blocks,
      rankingsByBlock,
      loading,
      refreshing,
      error,
      today,
      nowTime,
      getRanking,
      getIntensityCategoryName,
      getAgentCategory,
      fetchParticipantsForSchedules,
    ],
  );

  useEffect(() => {
    let mounted = true;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void loadAll();
      }
    };

    async function loadAll(isRefresh = false) {
      if (!mounted) return;
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      if (isRefresh) {
        setRefreshing(true);
        participantsCacheRef.current = {};
      } else {
        setLoading(true);
      }
      setError(null);

      const minimumSpin = new Promise((res) => setTimeout(res, 2000));
      try {
        const [serverDateTime, cats, wodTs, intensities] = await Promise.all([
          fetchCurrentDateTime().catch(() => null),
          fetchTrainingCategories(boxId),
          fetchCategoryWods(boxId),
          fetchIntensityCategories(),
        ]);

        if (serverDateTime) {
          // Format esperado: "2026-01-21 14:23:56"
          const [sDatePart, sTimePart] = serverDateTime.split(' ');
          const serverDateObj = new Date(serverDateTime.replace(' ', 'T'));

          if (!isNaN(serverDateObj.getTime())) {
            const newOffset = serverDateObj.getTime() - Date.now();
            timeOffsetRef.current = newOffset;
            console.log(`[DataContext] Server time sync. Offset: ${newOffset}ms`);

            setToday(sDatePart);
            setNowTime(sTimePart);
          }
        } else {
          // Fallback a fecha local si falla el endpoint
          setToday(getLocalDate());
          setNowTime(new Date().toTimeString().slice(0, 8));
        }

        const wodData = await fetchWodDelDia('now', boxId);

        if (!mounted) return;

        setCategoriesAgent(cats);
        setCategoryWods(wodTs);
        setIntensityCategories(intensities);
        setWodDelDia(wodData);

        const allParsedBlocks: Block[] = Array.isArray(wodData)
          ? wodData.flatMap((w) =>
            Array.isArray(w.blocks) ? w.blocks.map((b: any) => parseBlock(b)) : [],
          )
          : [];

        if (!mounted) return;
        setBlocks(allParsedBlocks);

        // Construir rankings con control de concurrencia (usando intensities)
        const nuevosRankings: Record<number, Record<number, Record<number, RankingItem[]>>> = {};
        const rankingPromises: Promise<void>[] = [];

        setRankingsByBlock(nuevosRankings);
        setParticipantsVersion((v) => v + 1);

        (allParsedBlocks ?? []).forEach((block) => {
          nuevosRankings[block.id] = {};
          (intensities ?? []).forEach((intCat) => {
            nuevosRankings[block.id][intCat.id] = {};
            (block.ratingTypes ?? []).forEach((rt) => {
              const p = limit(async () => {
                const ranks = await fetchRanking(block.id, intCat.id, rt.id).catch((e) => {
                  console.warn(`Ranking error [block ${block.id}]`, e);
                  return [] as RankingItem[];
                });
                nuevosRankings[block.id][intCat.id][rt.id] = ranks;
              });
              rankingPromises.push(p);
            });
          });
        });

        await Promise.all(rankingPromises);
        await minimumSpin;

        if (!mounted) return;
        setRankingsByBlock(nuevosRankings);
      } catch (err: any) {
        if (!mounted) return;
        if (err instanceof UnauthorizedError) setError('Debes iniciar sesión');
        else if (err instanceof ForbiddenError) setError('No tienes permiso');
        else if (err instanceof NotFoundError) setError('Recurso no encontrado');
        else if (err instanceof NetworkError) setError('Error de red');
        else if (err instanceof ApiError) setError(`Error servidor: ${err.message}`);
        else if (err instanceof InvalidResponseError) setError('Respuesta inválida');
        else if (err instanceof NoWodError) setError(err.message);
        else setError('Error inesperado');
      } finally {
        if (mounted) {
          if (isRefresh) {
            setRefreshing(false);
          } else {
            setLoading(false);
          }
        }
        isFetchingRef.current = false;
      }
    }

    // limpia cache / ref si quieres
    participantsCacheRef.current = {};

    // primera carga
    void loadAll(false);

    // Programamos reload cada 90s
    const intervalId = window.setInterval(() => {
      void loadAll(true);
    }, 90_000);

    // escuchar visibilitychange para forzar refetch cuando vuelve a visible
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [boxId, limit]);

  return (
    // Proveer el contexto con todos los datos y loading
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
};

// Hook personalizado para consumir el contexto de datos
export const useDataContext = (): DataContextProps => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext debe usarse dentro de DataProvider');
  }
  return context;
};
